import { Driver } from '../../types/cms.types';
import { DriverService } from '../../service/supabase/DriverService';
import IBloc from '../../base/IBloc';

export interface DriverFilter {
    search?: string;
    status?: string;
    vehicle_type?: string;
    city_of_operation?: string;
    is_online?: boolean;
}

export interface DriverState {
    drivers: Driver[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    loading: boolean;
    error: string | null;
    filters: DriverFilter;
}

export interface DriverViewState {
    drivers: Driver[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    loading: boolean;
    error: string | null;
    filters: DriverFilter;
    hasData: boolean;
}

export class BlocManagementDrivers extends IBloc {
    private driverService: DriverService;
    
    /**
     * State chính của Drivers Management
     */
    info: DriverViewState = {
        drivers: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        loading: false,
        error: null,
        filters: {},
        hasData: false
    };

    constructor() {
        super();
        this.driverService = DriverService.getInstance();
    }

    /**
     * Lấy dữ liệu ban đầu
     */
    async initData(): Promise<void> {
        this.updateInfo({ loading: true, error: null });

        try {
            await this.loadDrivers();
        } catch (error: any) {
            console.error('Error initializing drivers data:', error);
            this.updateInfo({
                loading: false,
                error: error.message || 'Có lỗi xảy ra khi khởi tạo dữ liệu tài xế'
            });
        }
    }

    /**
     * Load drivers với pagination và filters
     */
    async loadDrivers(): Promise<void> {
        this.updateInfo({ loading: true, error: null });

        try {
            const response = await this.driverService.getDrivers(
                this.info.pagination.page,
                this.info.pagination.limit,
                this.info.filters
            );

            if (response.success && response.data) {
                this.updateInfo({
                    drivers: response.data,
                    pagination: response.pagination,
                    loading: false,
                    error: null,
                    hasData: response.data.length > 0
                });
            } else {
                this.updateInfo({
                    loading: false,
                    error: response.error || 'Không thể tải danh sách tài xế',
                    drivers: [],
                    hasData: false
                });
            }
        } catch (error: any) {
            console.error('Error loading drivers:', error);
            this.updateInfo({
                loading: false,
                error: error.message || 'Có lỗi xảy ra khi tải danh sách tài xế',
                drivers: [],
                hasData: false
            });
        }
    }

    /**
     * Filter theo status
     */
    async filterByStatus(status: string): Promise<void> {
        const newFilters = { ...this.info.filters, status };
        this.updateInfo({ 
            filters: newFilters, 
            pagination: { ...this.info.pagination, page: 1 } 
        });
        await this.loadDrivers();
    }

    /**
     * Filter theo vehicle type
     */
    async filterByVehicleType(vehicleType: string): Promise<void> {
        const newFilters = { ...this.info.filters, vehicle_type: vehicleType };
        this.updateInfo({ 
            filters: newFilters,
            pagination: { ...this.info.pagination, page: 1 } 
        });
        await this.loadDrivers();
    }

    /**
     * Filter theo city
     */
    async filterByCity(city: string): Promise<void> {
        const newFilters = { ...this.info.filters, city_of_operation: city };
        this.updateInfo({ 
            filters: newFilters,
            pagination: { ...this.info.pagination, page: 1 } 
        });
        await this.loadDrivers();
    }

    /**
     * Filter theo online status
     */
    async filterByOnlineStatus(isOnline: boolean | undefined): Promise<void> {
        const newFilters = { ...this.info.filters, is_online: isOnline };
        this.updateInfo({ 
            filters: newFilters,
            pagination: { ...this.info.pagination, page: 1 } 
        });
        await this.loadDrivers();
    }

    /**
     * Search drivers
     */
    async searchDrivers(searchTerm: string): Promise<void> {
        const newFilters = { ...this.info.filters, search: searchTerm };
        this.updateInfo({ 
            filters: newFilters,
            pagination: { ...this.info.pagination, page: 1 } 
        });
        await this.loadDrivers();
    }

    /**
     * Clear all filters
     */
    async clearFilters(): Promise<void> {
        this.updateInfo({ 
            filters: {},
            pagination: { ...this.info.pagination, page: 1 } 
        });
        await this.loadDrivers();
    }

    /**
     * Change page
     */
    async changePage(page: number): Promise<void> {
        this.updateInfo({
            pagination: { ...this.info.pagination, page }
        });
        await this.loadDrivers();
    }

    /**
     * Change page size
     */
    async changePageSize(limit: number): Promise<void> {
        this.updateInfo({
            pagination: { ...this.info.pagination, limit, page: 1 }
        });
        await this.loadDrivers();
    }

    /**
     * Refresh data
     */
    async refresh(): Promise<void> {
        await this.loadDrivers();
    }

    /**
     * Delete driver
     */
    async deleteDriver(driverId: string): Promise<void> {
        try {
            const response = await this.driverService.deleteDriver(driverId);
            
            if (response.success) {
                // Reload data after successful delete
                await this.loadDrivers();
            } else {
                throw new Error(response.error || 'Không thể xóa tài xế');
            }
        } catch (error: any) {
            console.error('Error deleting driver:', error);
            throw error;
        }
    }

    /**
     * Update driver status
     */
    async updateDriverStatus(driverId: string, status: 'approved' | 'rejected' | 'pending'): Promise<void> {
        try {
            const response = await this.driverService.updateDriverStatus(driverId, status);
            
            if (response.success) {
                // Reload data after successful update
                await this.loadDrivers();
            } else {
                throw new Error(response.error || 'Không thể cập nhật trạng thái tài xế');
            }
        } catch (error: any) {
            console.error('Error updating driver status:', error);
            throw error;
        }
    }

    /**
     * Create new driver
     */
    async createDriver(driverData: Omit<Driver, 'id' | 'created_at' | 'updated_at'>): Promise<void> {
        try {
            const response = await this.driverService.createDriver(driverData);
            
            if (response.success) {
                // Reload data after successful create
                await this.loadDrivers();
            } else {
                throw new Error(response.error || 'Không thể tạo tài xế mới');
            }
        } catch (error: any) {
            console.error('Error creating driver:', error);
            throw error;
        }
    }

    /**
     * Update driver
     */
    async updateDriver(driverId: string, updates: Partial<Driver>): Promise<void> {
        try {
            const response = await this.driverService.updateDriver(driverId, updates);
            
            if (response.success) {
                // Reload data after successful update
                await this.loadDrivers();
            } else {
                throw new Error(response.error || 'Không thể cập nhật thông tin tài xế');
            }
        } catch (error: any) {
            console.error('Error updating driver:', error);
            throw error;
        }
    }

    /**
     * Update driver status with activation and expiry dates
     */
    async updateDriverStatusWithDates(
        driverId: string, 
        status: 'approved' | 'rejected' | 'pending',
        activationDate?: string,
        expiryDate?: string
    ): Promise<void> {
        try {
            const updates: Partial<Driver> = { status };
            
            if (status === 'approved' && activationDate && expiryDate) {
                updates.activation_date = activationDate;
                updates.expiry_date = expiryDate;
            } else if (status === 'rejected') {
                // Clear dates when rejecting
                updates.activation_date = undefined;
                updates.expiry_date = undefined;
            }

            const response = await this.driverService.updateDriver(driverId, updates);
            
            if (response.success) {
                // Reload data after successful update
                await this.loadDrivers();
            } else {
                throw new Error(response.error || 'Không thể cập nhật trạng thái tài xế');
            }
        } catch (error: any) {
            console.error('Error updating driver status with dates:', error);
            throw error;
        }
    }

    /**
     * Get current pagination info
     */
    get currentPagination() {
        return this.info.pagination;
    }

    /**
     * Get current filters
     */
    get currentFilters() {
        return this.info.filters;
    }

    /**
     * Update info state
     */
    private updateInfo(newInfo: Partial<DriverViewState>): void {
        this.info = { ...this.info, ...newInfo };
        this.setStream('info', this.info);
    }

    /**
     * Dispose resources
     */
    dispose(): void {
        super.dispose();
    }
}
